// src/pages/KarigarManagement/Payment/PaymentDetailPage.jsx
// Fixed: processPayment signature, added reject handler

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePaymentManagement } from "@/hooks/useKarigar";
import { Button } from "@/components/ui-v2/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui-v2/card";
import { Badge } from "@/components/ui-v2/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui-v2/alert-dialog";
import { Textarea } from "@/components/ui-v2/textarea";
import { Label } from "@/components/ui-v2/label";
import { Input } from "@/components/ui-v2/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui-v2/select";
import {
  ArrowLeft,
  User,
  Briefcase,
  CheckCircle,
  XCircle,
  IndianRupee,
  Calendar,
  CreditCard,
  Building,
  Loader2,
} from "lucide-react";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { toast } from "sonner";
import { format } from "date-fns";

// Status colors - handle both lowercase and uppercase
const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-blue-100 text-blue-800",
  processing: "bg-purple-100 text-purple-800",
  processed: "bg-green-100 text-green-800",
  paid: "bg-green-200 text-green-900",
  completed: "bg-green-200 text-green-900",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
  PENDING: "bg-yellow-100 text-yellow-800",
  APPROVED: "bg-blue-100 text-blue-800",
  PROCESSING: "bg-purple-100 text-purple-800",
  PROCESSED: "bg-green-100 text-green-800",
  PAID: "bg-green-200 text-green-900",
  COMPLETED: "bg-green-200 text-green-900",
  REJECTED: "bg-red-100 text-red-800",
  CANCELLED: "bg-gray-100 text-gray-800",
};

const PAYMENT_METHODS = [
  { value: "CASH", label: "Cash" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "UPI", label: "UPI" },
  { value: "CHEQUE", label: "Cheque" },
  { value: "NEFT", label: "NEFT" },
  { value: "RTGS", label: "RTGS" },
  { value: "IMPS", label: "IMPS" },
];

const PaymentDetailPage = () => {
  const navigate = useNavigate();
  const { paymentId } = useParams();

  const {
    currentPayment,
    loading,
    error,
    successMessage,
    getPaymentById,
    processPayment,
    reject, // ✅ Added reject function
    approve, // ✅ Added approve function (optional)
    clearError,
    clearSuccessMessage,
    clearPayment,
  } = usePaymentManagement();

  const [showProcessDialog, setShowProcessDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [processMethod, setProcessMethod] = useState("BANK_TRANSFER");
  const [processNotes, setProcessNotes] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [processing, setProcessing] = useState(false);

  // Fetch payment details
  useEffect(() => {
    if (paymentId) {
      getPaymentById(paymentId);
    }
    return () => {
      clearPayment();
    };
  }, [paymentId]);

  // Handle messages
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      clearSuccessMessage();
      // Refresh payment data after success
      if (paymentId) {
        getPaymentById(paymentId);
      }
    }
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [successMessage, error]);

  // ✅ Fixed: Handle process payment with correct signature
  const handleProcess = async () => {
    setProcessing(true);
    try {
      // processPayment(paymentId, paymentMethod, transactionId, referenceNumber, notes)
      await processPayment(
        paymentId,
        processMethod,
        transactionId || undefined,
        referenceNumber || undefined,
        processNotes || undefined
      );
      setShowProcessDialog(false);
      // Reset form
      setProcessMethod("BANK_TRANSFER");
      setTransactionId("");
      setReferenceNumber("");
      setProcessNotes("");
    } catch (err) {
      // Error is handled by useEffect above
    } finally {
      setProcessing(false);
    }
  };

  // ✅ Added: Handle reject payment
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    setProcessing(true);
    try {
      await reject(paymentId, rejectReason);
      setShowRejectDialog(false);
      setRejectReason("");
    } catch (err) {
      // Error is handled by useEffect above
    } finally {
      setProcessing(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Check if payment can be processed
  const canProcess = (status) => {
    const s = status?.toLowerCase();
    return s === "pending" || s === "approved";
  };

  if (loading.detail) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!currentPayment) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Payment not found</p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const payment = currentPayment;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">
                {payment.paymentNumber || payment._id?.slice(-8)}
              </h1>
              <Badge className={STATUS_COLORS[payment.status] || "bg-gray-100"}>
                {payment.status?.toUpperCase()}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              {payment.paymentType?.replace(/_/g, " ")}
            </p>
          </div>
        </div>

        {canProcess(payment.status) && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowRejectDialog(true)}>
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button onClick={() => setShowProcessDialog(true)}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Process Payment
            </Button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Amount Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Payment Amount</p>
                <p className="text-4xl font-bold text-primary">
                  {formatCurrency(
                    payment.amount || payment.grossAmount || payment.netAmount
                  )}
                </p>
                {payment.deductions > 0 && (
                  <p className="text-sm text-red-500 mt-1">
                    Deductions: -{formatCurrency(payment.deductions)}
                  </p>
                )}
                {payment.netAmount && payment.netAmount !== payment.amount && (
                  <p className="text-lg font-semibold mt-2">
                    Net: {formatCurrency(payment.netAmount)}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Payment Type</p>
                  <p className="font-medium">
                    {payment.paymentType?.replace(/_/g, " ")}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge
                    className={STATUS_COLORS[payment.status] || "bg-gray-100"}
                  >
                    {payment.status?.toUpperCase()}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Created Date</p>
                  <p className="font-medium">
                    {payment.createdAt
                      ? format(
                          new Date(payment.createdAt),
                          "dd MMM yyyy, hh:mm a"
                        )
                      : "-"}
                  </p>
                </div>
                {payment.processedAt && (
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Processed Date
                    </p>
                    <p className="font-medium">
                      {format(
                        new Date(payment.processedAt),
                        "dd MMM yyyy, hh:mm a"
                      )}
                    </p>
                  </div>
                )}
                {payment.paidAt && (
                  <div>
                    <p className="text-sm text-muted-foreground">Paid Date</p>
                    <p className="font-medium">
                      {format(new Date(payment.paidAt), "dd MMM yyyy, hh:mm a")}
                    </p>
                  </div>
                )}
              </div>

              {/* Transaction Info - if processed */}
              {payment.transaction && (
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm font-medium mb-3">
                    Transaction Details
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {payment.transaction.paymentMethod && (
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Payment Method
                        </p>
                        <p className="font-medium">
                          {payment.transaction.paymentMethod?.replace(
                            /_/g,
                            " "
                          )}
                        </p>
                      </div>
                    )}
                    {(payment.transaction.transactionId ||
                      payment.transactionId) && (
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Transaction ID
                        </p>
                        <p className="font-medium font-mono">
                          {payment.transaction.transactionId ||
                            payment.transactionId}
                        </p>
                      </div>
                    )}
                    {(payment.transaction.transactionReference ||
                      payment.referenceNumber) && (
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Reference Number
                        </p>
                        <p className="font-medium font-mono">
                          {payment.transaction.transactionReference ||
                            payment.referenceNumber}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Method - if directly on payment */}
              {payment.paymentMethod && !payment.transaction && (
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm font-medium mb-3">Payment Method</p>
                  <Badge variant="outline">
                    {payment.paymentMethod?.replace(/_/g, " ")}
                  </Badge>
                </div>
              )}

              {/* Notes */}
              {payment.notes && (
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="mt-1">{payment.notes}</p>
                </div>
              )}

              {/* Rejection Reason */}
              {payment.rejectionReason && (
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-red-600 font-medium">
                    Rejection Reason
                  </p>
                  <p className="mt-1 text-red-700">{payment.rejectionReason}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Breakdown (if available) */}
          {payment.breakdown && payment.breakdown.items?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Payment Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {payment.breakdown.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span>{item.description}</span>
                      <span className="font-medium">
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-3 border-t font-semibold">
                    <span>Total</span>
                    <span>
                      {formatCurrency(
                        payment.amount ||
                          payment.grossAmount ||
                          payment.netAmount
                      )}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Karigar Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-4 w-4" />
                Karigar
              </CardTitle>
            </CardHeader>
            <CardContent>
              {payment.karigar ? (
                <div
                  className="cursor-pointer hover:bg-muted/50 p-2 rounded -m-2"
                  onClick={() =>
                    navigate(
                      `/karigar-management/karigars/${
                        payment.karigar._id || payment.karigar.karigarId
                      }`
                    )
                  }
                >
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-lg font-medium">
                        {payment.karigar.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold">{payment.karigar.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {payment.karigar.karigarCode}
                      </p>
                    </div>
                  </div>
                </div>
              ) : payment.karigarId ? (
                <p className="text-muted-foreground">
                  Karigar ID:{" "}
                  {typeof payment.karigarId === "string"
                    ? payment.karigarId
                    : payment.karigarId._id}
                </p>
              ) : (
                <p className="text-muted-foreground">No karigar info</p>
              )}
            </CardContent>
          </Card>

          {/* Job Info */}
          {(payment.job || payment.jobId) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Related Job
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="cursor-pointer p-3 bg-muted/50 rounded hover:bg-muted"
                  onClick={() =>
                    navigate(
                      `/karigar-management/jobs/${
                        payment.job?._id || payment.job?.jobId || payment.jobId
                      }`
                    )
                  }
                >
                  <p className="font-medium">
                    {payment.job?.jobNumber || "View Job"}
                  </p>
                  {payment.job?.title && (
                    <p className="text-sm text-muted-foreground">
                      {payment.job.title}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bank Details */}
          {payment.karigar?.bankDetails && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Bank Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {payment.karigar.bankDetails.accountHolderName && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Account Name</span>
                    <span className="font-medium">
                      {payment.karigar.bankDetails.accountHolderName}
                    </span>
                  </div>
                )}
                {payment.karigar.bankDetails.accountNumber && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Account No</span>
                    <span className="font-medium font-mono">
                      {payment.karigar.bankDetails.accountNumber}
                    </span>
                  </div>
                )}
                {payment.karigar.bankDetails.bankName && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Bank</span>
                    <span className="font-medium">
                      {payment.karigar.bankDetails.bankName}
                    </span>
                  </div>
                )}
                {payment.karigar.bankDetails.ifscCode && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">IFSC</span>
                    <span className="font-medium font-mono">
                      {payment.karigar.bankDetails.ifscCode}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Audit Trail */}
          {payment.auditTrail && payment.auditTrail.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {payment.auditTrail.slice(0, 5).map((entry, idx) => (
                    <div key={idx} className="text-sm">
                      <p className="font-medium">{entry.action}</p>
                      <p className="text-muted-foreground text-xs">
                        {entry.timestamp
                          ? format(
                              new Date(entry.timestamp),
                              "dd MMM yyyy, hh:mm a"
                            )
                          : ""}
                        {entry.by && ` by ${entry.by}`}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Process Payment Dialog */}
      <AlertDialog open={showProcessDialog} onOpenChange={setShowProcessDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Process Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Process payment of{" "}
              {formatCurrency(
                payment.amount || payment.grossAmount || payment.netAmount
              )}{" "}
              to {payment.karigar?.name || "karigar"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Payment Method *</Label>
              <Select value={processMethod} onValueChange={setProcessMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method.value} value={method.value}>
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Transaction ID (Optional)</Label>
              <Input
                placeholder="TXN123456789"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
              />
            </div>
            <div>
              <Label>Reference Number (Optional)</Label>
              <Input
                placeholder="REF001"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
              />
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Any notes about this payment..."
                value={processNotes}
                onChange={(e) => setProcessNotes(e.target.value)}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleProcess} disabled={processing}>
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                "Confirm Payment"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Dialog */}
      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reject this payment of{" "}
              {formatCurrency(
                payment.amount || payment.grossAmount || payment.netAmount
              )}
              ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label>Reason for Rejection *</Label>
            <Textarea
              placeholder="Enter reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleReject} // ✅ Added onClick handler
              disabled={!rejectReason.trim() || processing}
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Rejecting...
                </>
              ) : (
                "Reject Payment"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PaymentDetailPage;
